import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import Button from "@/components/ui/Button";
import PageShell from "@/components/ui/PageShell";

type MembershipInterval = "monthly" | "annual";

export function Subscribe() {
  const theme = usePalette();
  const [interval, setInterval] = useState<MembershipInterval>("monthly");
  const [plans, setPlans] = useState<Record<MembershipInterval, boolean>>({
    monthly: true,
    annual: false,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ plans: Record<MembershipInterval, boolean> }>("/api/account/membership-plans")
      .then((r) => {
        setPlans(r.plans);
        if (!r.plans.monthly && r.plans.annual) setInterval("annual");
      })
      .catch(() => {
        /* subscribe will surface configuration errors */
      });
  }, []);

  async function start() {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.post<{ url: string }>("/api/account/subscribe", { interval });
      window.location.href = r.url;
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to start checkout");
      setBusy(false);
    }
  }

  return (
    <PageShell title="Become a member" eyebrow="Community">
      <p
        className="mb-8"
        style={{
          fontFamily: fonts.body,
          fontWeight: 500,
          color: `${colors.navy}bb`,
          letterSpacing: "0.03em",
        }}
      >
        Membership unlocks the private Discord community.
      </p>

      <div className="grid gap-3 max-w-md mb-6">
        {(["monthly", "annual"] as const).map((plan) => {
          const selected = interval === plan;
          const available = plans[plan];
          return (
            <button
              key={plan}
              type="button"
              disabled={!available || busy}
              onClick={() => setInterval(plan)}
              className="rounded-2xl text-left transition-all duration-200 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-55"
              style={{
                padding: "1rem 1.25rem",
                border: selected ? `2px solid ${theme.accent}` : `1px solid ${colors.tan}40`,
                backgroundColor: selected ? colors.blushLight : colors.cream,
                fontFamily: fonts.body,
                color: colors.navy,
                cursor: available && !busy ? "pointer" : "not-allowed",
              }}
            >
              <span
                className="block text-sm"
                style={{ fontWeight: 700, letterSpacing: "0.03em" }}
              >
                {plan === "monthly" ? "Monthly" : "Annual"}
              </span>
              <span
                className="block text-xs mt-1"
                style={{ fontWeight: 500, color: `${colors.navy}88`, letterSpacing: "0.03em" }}
              >
                {available ? "Continue to Stripe checkout" : "Not configured yet"}
              </span>
            </button>
          );
        })}
      </div>

      {err && (
        <p
          className="mb-4 text-sm"
          style={{ color: colors.berryRose, fontFamily: fonts.body, fontWeight: 600 }}
        >
          {err}
        </p>
      )}

      <Button onClick={start} disabled={busy || !plans[interval]}>
        {busy ? "Redirecting…" : "Subscribe via Stripe"}
      </Button>
    </PageShell>
  );
}
